import { useTranslation } from 'react-i18next'
import { Form, Formik, FormikHelpers } from 'formik'
import { useHistory } from 'react-router'
import { useDispatch } from 'react-redux'
import { omit, map, prop, equals } from 'ramda'
import { Stack, Heading, useResponsiveValue, useToast } from '@machobear/design-system'

import PhotoSection from './photo-section'
import VideoSection from './video-section'
import TourSection from './tour-section'
import { mediaValidationSchema } from '../util/vallidationSchema'
import useRedirectPublishedListing from '../hooks/use-redirect-published-listing'
import FormFooter from '../components/footer'
import { MediaFormValues, Listing } from '../types'
import FormWrapper from '../form-wrapper'

const ListingMediaForm = ({ data, isLoading }: { data: Listing; isLoading: boolean }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const showToast = useToast()

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  useRedirectPublishedListing(data)

  const initialValues: MediaFormValues = {
    ...(omit(['state_machine'], data) as MediaFormValues)
  }

  const saveListing = async (values: MediaFormValues) => {
    const oldImageOrder = map(prop('id'), data.images)
    const newImageOrder = map(prop('id'), values.images)

    // If the images have been reordered, update on the listing
    if (values.images && values?.images?.length > 0 && !equals(oldImageOrder, newImageOrder)) {
      const { response: sortResponse } = await dispatch.listing.sortImages(values.images)
      if (!sortResponse.ok) return sortResponse
    }

    const { response } = await dispatch.listing.updateUnit(values)

    return response
  }

  const handleSaveDraft = async (
    values: MediaFormValues,
    setSubmitting: FormikHelpers<MediaFormValues>['setSubmitting']
  ) => {
    setSubmitting(true)
    const response = await saveListing(values)
    if (response.ok) {
      showToast({
        message: t('l.listing.edit.listing_edited_toast_text', 'Your listing has been saved!'),
        tone: 'positive'
      })
      history.push('/listings')
    }
    setSubmitting(false)
  }

  const onSubmit = async (
    values: MediaFormValues,
    { setSubmitting }: FormikHelpers<MediaFormValues>
  ) => {
    const response = await saveListing(values)
    if (response.ok) {
      history.push(`/listing/create/${data.listing_id}/summary`)
    }
    setSubmitting(false)
  }

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={mediaValidationSchema}
        enableReinitialize
      >
        {({ values, isSubmitting, setSubmitting }) => {
          return (
            <FormWrapper>
              <Form>
                <Stack align="left" space={{ mobile: 'gutter', desktop: 'large' }}>
                  {!isMobile && (
                    <Heading level="4" component="h2">
                      {t('Media', 'Media')}
                    </Heading>
                  )}
                  <PhotoSection data={data} isLoading={isLoading} />
                  <VideoSection isLoading={isLoading} />
                  <TourSection isLoading={isLoading} />
                  <FormFooter
                    isLoading={isLoading}
                    onSave={() => handleSaveDraft(values, setSubmitting)}
                    isSubmitting={isSubmitting}
                    step="listing-media"
                  />
                </Stack>
              </Form>
            </FormWrapper>
          )
        }}
      </Formik>
    </>
  )
}

export default ListingMediaForm
